import {
  SendOutlined,
  TeamOutlined,
  UserOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Typography,
} from "antd";
import { useState } from "react";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TARGET_OPTIONS = [
  {
    value: "all-customers",
    label: "All Agents",
    icon: <TeamOutlined />,
    description: "Send to every Agent",
  },
  // {
  //   value: "specific-customer",
  //   label: "Specific Customer",
  //   icon: <UserOutlined />,
  //   description: "Send to a single customer by their ID",
  // },
  {
    value: "all-hotels",
    label: "All Hotel Partners",
    icon: <HomeOutlined />,
    description: "Send to all registered hotel partners",
  },
];

const NOTIFICATION_TYPES = [
  { value: "general", label: "General" },
  { value: "system", label: "System" },
  { value: "alert", label: "Alert" },
  { value: "promo", label: "Promo" },
  { value: "booking", label: "Booking" },
];

function SendNotification() {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [target, setTarget] = useState("all-customers");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (values) => {
    const { title, message, type, customerId } = values;

    let url = "";
    let body = { title, message, type: type || "general" };

    if (target === "all-customers") {
      url = apiPath.sendNotificationToAllAgents;
    } else if (target === "specific-customer") {
      if (!customerId?.trim()) {
        ShowToast("Please enter a Customer ID", Severty.ERROR);
        return;
      }
      url = apiPath.sendNotificationToCustomer;
      body.customerId = customerId.trim();
    } else if (target === "all-hotels") {
      url = apiPath.sendNotificationToAllHotels;
    }

    setLoading(true);
    request({
      url,
      method: "POST",
      data: body,
      onSuccess: (data) => {
        setLoading(false);
        if (data?.success) {
          const sent = data.count ?? (data.notification ? 1 : 0);
          ShowToast(
            data.message || `Notification${sent !== 1 ? "s" : ""} sent successfully`,
            Severty.SUCCESS
          );
          form.resetFields(["title", "message", "type", "customerId"]);
        } else {
          ShowToast(data?.message || "Failed to send notification", Severty.ERROR);
        }
      },
      onError: (err) => {
        setLoading(false);
        ShowToast(
          err?.response?.data?.message || "Failed to send notification",
          Severty.ERROR
        );
      },
    });
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          Send Notification
        </Title>
        <Text type="secondary">
          Broadcast messages to customers or hotel partners
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* Target selection */}
        <Col span={24}>
          <Card title="Select Target Audience" bordered={false}>
            <Radio.Group
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                form.resetFields(["customerId"]);
              }}
              style={{ width: "100%" }}
            >
              <Row gutter={[16, 16]}>
                {TARGET_OPTIONS.map((opt) => (
                  <Col key={opt.value} xs={24} sm={8}>
                    <Radio.Button
                      value={opt.value}
                      style={{
                        width: "100%",
                        height: "auto",
                        padding: "16px",
                        borderRadius: 8,
                        textAlign: "left",
                        whiteSpace: "normal",
                        lineHeight: "normal",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 20 }}>{opt.icon}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            {opt.label}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: "#8c8c8c",
                              marginTop: 2,
                            }}
                          >
                            {opt.description}
                          </div>
                        </div>
                      </div>
                    </Radio.Button>
                  </Col>
                ))}
              </Row>
            </Radio.Group>
          </Card>
        </Col>

        {/* Compose form */}
        <Col span={24}>
          <Card title="Compose Message" bordered={false}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ type: "general" }}
            >
              {target === "specific-customer" && (
                <Form.Item
                  label="Customer ID"
                  name="customerId"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the customer's MongoDB ID",
                    },
                  ]}
                >
                  <Input placeholder="Enter Customer ObjectId (e.g. 64a3f2...)" />
                </Form.Item>
              )}

              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                      { required: true, message: "Please enter a title" },
                    ]}
                  >
                    <Input placeholder="Notification title" maxLength={100} showCount />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Type" name="type">
                    <Select>
                      {NOTIFICATION_TYPES.map((t) => (
                        <Option key={t.value} value={t.value}>
                          {t.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Message"
                name="message"
                rules={[
                  { required: true, message: "Please enter a message" },
                ]}
              >
                <TextArea
                  placeholder="Write your notification message here..."
                  rows={4}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SendOutlined />}
                  size="large"
                >
                  Send Notification
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default SendNotification;
